import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type WorkspaceMembership = Awaited<ReturnType<typeof trpc.workspaces.list.query>>[number];

export function useWorkspaces() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const workspacesQuery = useQuery(trpc.workspaces.list.queryOptions());
  const switchMutation = useMutation(trpc.workspaces.switchActive.mutationOptions());
  const createMutation = useMutation(trpc.workspaces.create.mutationOptions());
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      await workspacesQuery.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load workspaces.");
    }
  }, [workspacesQuery.refetch]);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    queryClient.setQueryData(trpc.workspaces.list.queryKey(), await switchMutation.mutateAsync({ workspaceId }));
  }, [queryClient, switchMutation, trpc]);

  const createWorkspace = useCallback(async (name: string) => {
    const result = await createMutation.mutateAsync({ name });
    queryClient.setQueryData(trpc.workspaces.list.queryKey(), result.workspaces);
    return result.workspaceId;
  }, [createMutation, queryClient, trpc]);

  return {
    createWorkspace,
    data: workspacesQuery.data ?? [],
    error: error ?? (workspacesQuery.error instanceof Error ? workspacesQuery.error.message : workspacesQuery.error ? String(workspacesQuery.error) : null),
    reload,
    switchWorkspace,
  };
}
